import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { userGoogleLogin } from "../../Action/api.js";
import { GoogleLogin } from "@react-oauth/google";
import CLogo from "../../Components/Reusable/Logo";
import PhoneNumberDialog from "../../Components/PhoneNumberDialog";
import { decodeJWT } from "../../Utils";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CampaignIcon from "@mui/icons-material/Campaign";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SecurityIcon from "@mui/icons-material/Security";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

// ─── Auth helper ──────────────────────────────────────────────────────────────
const storeAuthData = (data) => {
  const userData = data.userData || {};
  const { menuList = [] } = userData;

  localStorage.setItem("authToken", data.authorizationToken);
  localStorage.setItem("isAuthenticated", "true");
  localStorage.setItem("loginTime", Date.now().toString());

  const first = menuList.find((m) => m.readAccess === "yes" || m.writeAccess === "yes");
  const menuToRoute = {
    "MENU-001": "/dashboard",
    "MENU-002": "/run-post",
    "MENU-003": "/safe-post",
    "MENU-004": "/email-campaign",
    "MENU-005": "/sms-campaign",
    "MENU-006": "/approvals",
    "MENU-007": "/users",
    "MENU-008": "/settings",
  };
  return (first && menuToRoute[first.menuId]) || "/dashboard";
};

// ─── Feature list for the left panel ─────────────────────────────────────────
const features = [
  {
    icon: <ScheduleIcon sx={{ fontSize: 20 }} />,
    title: "Smart Scheduling",
    desc: "Schedule posts across all platforms in one click",
  },
  {
    icon: <CampaignIcon sx={{ fontSize: 20 }} />,
    title: "Multi-Channel Campaigns",
    desc: "Email, SMS & social media from a single dashboard",
  },
  {
    icon: <TrendingUpIcon sx={{ fontSize: 20 }} />,
    title: "Analytics & Insights",
    desc: "Real-time performance tracking to grow faster",
  },
  {
    icon: <AutoAwesomeIcon sx={{ fontSize: 20 }} />,
    title: "AI-Powered Content",
    desc: "Generate captions, posts and campaigns with AI",
  },
];

const stats = [
  { value: "10k+", label: "Businesses" },
  { value: "50M+", label: "Posts Sent" },
  { value: "99.9%", label: "Uptime" },
];

// ─── Main Login component ─────────────────────────────────────────────────────
export const Login = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [btnWidth, setBtnWidth] = useState(300);
  const [phoneDialogOpen, setPhoneDialogOpen] = useState(false);
  const [pendingCredential, setPendingCredential] = useState(null);
  const btnContainerRef = useRef(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    const measure = () => {
      if (btnContainerRef.current) {
        setBtnWidth(btnContainerRef.current.offsetWidth);
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const handleGoogle = async (credentialResponse) => {
    setLoading(true);
    setError("");
    try {
      // First attempt login without phone number
      const response = await userGoogleLogin(credentialResponse.credential);
      
      // Debug: Log the full response to see what backend returns
      console.log("Login API Response:", response);
      console.log("Response data:", response?.data);
      
      // Decode the JWT token to get isNewUser field
      const token = response?.data?.authorizationToken;
      if (token) {
        const decodedToken = decodeJWT(token);
        console.log("Decoded token payload:", decodedToken);
        console.log("Is new user flag from token:", decodedToken?.isNewUser);
        
        // Backend sends "Yes" or "No" as string inside the token
        const isNewUser = decodedToken?.isNewUser === "Yes";
        
        console.log("Determined isNewUser:", isNewUser);
        
        // Show dialog ONLY for explicitly marked new users
        if (isNewUser === true) {
          // Store the credential for later use with phone number
          setPendingCredential(credentialResponse.credential);
          setPhoneDialogOpen(true);
          setLoading(false);
          console.log("Opening phone dialog for NEW user signup");
          return;
        }
      }
      
      // Existing user flow OR new user without phone requirement - proceed with navigation
      if (response?.data?.status) {
        console.log("Proceeding to dashboard (existing user or completed signup)");
        navigate(storeAuthData(response.data));
      } else {
        setError(
          response?.data?.uiMsg ||
            response?.response?.data?.uiMsg ||
            "Authentication failed. Please try again."
        );
      }
    } catch (err) {
      console.error("Login error:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneNumberSubmit = async (phoneNumber) => {
    setLoading(true);
    setError("");
    console.log("Submitting phone number:", phoneNumber);
    console.log("Using credential:", pendingCredential ? "exists" : "missing");
    
    try {
      // Call the login API again with phone number
      const response = await userGoogleLogin(pendingCredential, phoneNumber);
      
      console.log("Phone number submission response:", response);
      console.log("Phone response data:", response?.data);
      
      if (response?.data?.status) {
        console.log("Phone submission successful, closing dialog and navigating");
        setPhoneDialogOpen(false);
        navigate(storeAuthData(response.data));
      } else {
        setError(
          response?.data?.uiMsg ||
            response?.response?.data?.uiMsg ||
            "Failed to complete signup. Please try again."
        );
        setLoading(false);
      }
    } catch (err) {
      console.error("Phone submission error:", err);
      setError("Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  const handlePhoneDialogClose = () => {
    if (!loading) {
      setPhoneDialogOpen(false);
      setPendingCredential(null);
      setError("");
    }
  };

  // ── Mobile layout ────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", background: "#fff" }}>

        {/* Mobile gradient header strip */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #0f0c29 0%, #302b63 60%, #24243e 100%)",
            px: 3,
            pt: 3,
            pb: 4,
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* blob */}
          <Box sx={{ position: "absolute", width: 200, height: 200, borderRadius: "50%", background: "radial-gradient(circle, rgba(102,126,234,0.25) 0%, transparent 70%)", top: -60, right: -60, pointerEvents: "none" }} />

          {/* back link */}
          <Box
            onClick={() => navigate("/")}
            sx={{ display: "flex", alignItems: "center", gap: 0.6, color: "rgba(255,255,255,0.55)", cursor: "pointer", mb: 3, fontSize: "0.82rem", transition: "color 0.2s", "&:hover": { color: "#fff" }, userSelect: "none", width: "fit-content" }}
          >
            <ArrowBackIcon sx={{ fontSize: 15 }} />
            Back to home
          </Box>

          {/* brand */}
          <Box sx={{ mb: 1.5 }}>
            <CLogo size="42px" textSize="1.2rem" isDarkTheme={true} />
          </Box>

          <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: "0.8rem", lineHeight: 1.6 }}>
            Schedule, publish &amp; grow — all from one workspace.
          </Typography>
        </Box>

        {/* Mobile form area — scrollable */}
        <Box sx={{ flex: 1, overflowY: "auto", px: 3, pt: 4, pb: 5, display: "flex", flexDirection: "column", alignItems: "center" }}>
          <Box sx={{ width: "100%", maxWidth: 400 }}>

            {/* Get Started badge */}
            <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
              <Box sx={{
                display: "inline-flex", alignItems: "center", gap: 0.75,
                py: "8px", px: 2.5, borderRadius: "999px",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                boxShadow: "0 4px 14px rgba(102,126,234,0.45)",
                userSelect: "none",
              }}>
                <AutoAwesomeIcon sx={{ fontSize: 15, color: "#fff" }} />
                <Typography sx={{ fontWeight: 800, fontSize: "0.85rem", color: "#fff", letterSpacing: "0.04em" }}>
                  GET STARTED
                </Typography>
              </Box>
            </Box>

            {/* Heading */}
            <Typography sx={{ fontWeight: 800, fontSize: "1.4rem", color: "#0f172a", mb: 0.75, letterSpacing: "-0.025em", lineHeight: 1.25 }}>
              Welcome 👋
            </Typography>
            <Typography sx={{ color: "#6b7280", fontSize: "0.85rem", lineHeight: 1.7, mb: 3.5 }}>
              New or returning — just continue with Google and you're in.{" "}
              <Box component="span" sx={{ color: "#667eea", fontWeight: 700 }}>No password needed.</Box>
            </Typography>

            {/* Trust badges */}
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 3.5 }}>
              <Chip
                icon={<SecurityIcon sx={{ fontSize: "13px !important" }} />}
                label="Secure & Encrypted"
                size="small"
                sx={{ background: "#f0fdf4", color: "#15803d", border: "1px solid #bbf7d0", fontWeight: 600, fontSize: "0.7rem", height: 26, "& .MuiChip-icon": { color: "#15803d" } }}
              />
              <Chip
                icon={<CheckCircleIcon sx={{ fontSize: "13px !important" }} />}
                label="No credit card"
                size="small"
                sx={{ background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe", fontWeight: 600, fontSize: "0.7rem", height: 26, "& .MuiChip-icon": { color: "#1d4ed8" } }}
              />
            </Box>

            {/* Error */}
            {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

            {/* Google button */}
            <Box sx={{ border: "1.5px solid #e5e7eb", borderRadius: "14px", p: "14px", background: "#fafafa", mb: 3, boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }} ref={btnContainerRef}>
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}>
                  <CircularProgress size={28} sx={{ color: "#667eea" }} />
                </Box>
              ) : (
                <GoogleLogin
                  onSuccess={handleGoogle}
                  onError={() => setError("Google authentication failed. Please try again.")}
                  theme="outline"
                  size="large"
                  width={String(btnWidth - 28)}
                  text="continue_with"
                  shape="rectangular"
                  logo_alignment="center"
                />
              )}
            </Box>

            {/* OR divider */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
              <Box sx={{ flex: 1, height: "1px", background: "#f0f0f0" }} />
              <Typography sx={{ color: "#d1d5db", fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.05em" }}>OR</Typography>
              <Box sx={{ flex: 1, height: "1px", background: "#f0f0f0" }} />
            </Box>

            {/* More options pill */}
            <Box sx={{ textAlign: "center", py: 1.5, borderRadius: "10px", border: "1.5px dashed #e5e7eb", color: "#9ca3af", fontSize: "0.78rem", mb: 4, userSelect: "none" }}>
              More sign-in options coming soon
            </Box>

            {/* Legal */}
            <Typography sx={{ display: "block", textAlign: "center", color: "#9ca3af", lineHeight: 1.85, fontSize: "0.72rem" }}>
              New users get an account automatically.
              <br />
              By continuing you agree to our{" "}
              <Box component="span" onClick={() => navigate("/termsandconditions")} sx={{ color: "#667eea", cursor: "pointer", fontWeight: 600, "&:hover": { textDecoration: "underline" } }}>Terms of Service</Box>{" "}
              &amp;{" "}
              <Box component="span" onClick={() => navigate("/privacy-policy")} sx={{ color: "#667eea", cursor: "pointer", fontWeight: 600, "&:hover": { textDecoration: "underline" } }}>Privacy Policy</Box>.
            </Typography>

          </Box>
        </Box>

        {/* Phone Number Dialog */}
        <PhoneNumberDialog
          open={phoneDialogOpen}
          onSubmit={handlePhoneNumberSubmit}
          onClose={handlePhoneDialogClose}
          loading={loading}
        />
      </Box>
    );
  }

  // ── Desktop layout ────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "row",
        background: "#f8f9fc",
        overflow: "hidden",
      }}
    >
      {/* ── LEFT PANEL ───────────────────────────────────────────────────────── */}
      {(
        <Box
          sx={{
            flex: "0 0 55%",
            background: "linear-gradient(145deg, #0f0c29 0%, #302b63 45%, #24243e 100%)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "flex-start",
            p: { md: 7, lg: 10 },
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Decorative blurred circles */}
          <Box
            sx={{
              position: "absolute",
              width: 420,
              height: 420,
              borderRadius: "50%",
              background: "radial-gradient(circle, rgba(102,126,234,0.22) 0%, transparent 70%)",
              top: -120,
              right: -120,
              pointerEvents: "none",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              width: 300,
              height: 300,
              borderRadius: "50%",
              background: "radial-gradient(circle, rgba(118,75,162,0.28) 0%, transparent 70%)",
              bottom: 40,
              left: -90,
              pointerEvents: "none",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              width: 180,
              height: 180,
              borderRadius: "50%",
              background: "radial-gradient(circle, rgba(103,232,249,0.12) 0%, transparent 70%)",
              bottom: "40%",
              right: "8%",
              pointerEvents: "none",
            }}
          />

          {/* Back to home */}
          <Box
            onClick={() => navigate("/")}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              color: "rgba(255,255,255,0.5)",
              cursor: "pointer",
              mb: 7,
              fontSize: "0.85rem",
              transition: "color 0.2s",
              "&:hover": { color: "rgba(255,255,255,0.9)" },
              userSelect: "none",
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 16 }} />
            Back to home
          </Box>

          {/* Brand mark */}
          <Box sx={{ mb: 4 }}>
            <CLogo size="48px" textSize="1.45rem" isDarkTheme={true} />
          </Box>

          {/* Headline */}
          <Typography
            sx={{
              color: "#fff",
              fontWeight: 800,
              fontSize: { md: "2rem", lg: "2.6rem" },
              lineHeight: 1.2,
              letterSpacing: "-0.03em",
              mb: 2,
              maxWidth: 480,
            }}
          >
            Your all-in-one
            <Box
              component="span"
              sx={{
                display: "block",
                background: "linear-gradient(90deg, #a78bfa, #67e8f9)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Marketing Platform
            </Box>
          </Typography>

          <Typography
            sx={{
              color: "rgba(255,255,255,0.55)",
              fontSize: "0.95rem",
              lineHeight: 1.75,
              mb: 5,
              maxWidth: 420,
            }}
          >
            Schedule, publish, and analyse your content across every channel — all from one powerful workspace.
          </Typography>

          {/* Feature list */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2.5,
              mb: 6,
              width: "100%",
              maxWidth: 440,
            }}
          >
            {features.map((f) => (
              <Box key={f.title} sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                <Box
                  sx={{
                    mt: 0.1,
                    width: 36,
                    height: 36,
                    borderRadius: 2,
                    background: "rgba(255,255,255,0.07)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#a78bfa",
                    flexShrink: 0,
                  }}
                >
                  {f.icon}
                </Box>
                <Box>
                  <Typography
                    sx={{ color: "#fff", fontWeight: 700, fontSize: "0.88rem", lineHeight: 1.3 }}
                  >
                    {f.title}
                  </Typography>
                  <Typography
                    sx={{ color: "rgba(255,255,255,0.45)", fontSize: "0.78rem", lineHeight: 1.5 }}
                  >
                    {f.desc}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>

          {/* Divider */}
          <Box sx={{ width: "100%", maxWidth: 440, height: "1px", background: "rgba(255,255,255,0.08)", mb: 4 }} />

          {/* Stats */}
          <Box sx={{ display: "flex", gap: 5 }}>
            {stats.map((s) => (
              <Box key={s.label}>
                <Typography
                  sx={{ color: "#a78bfa", fontWeight: 800, fontSize: "1.5rem", lineHeight: 1.1 }}
                >
                  {s.value}
                </Typography>
                <Typography sx={{ color: "rgba(255,255,255,0.4)", fontSize: "0.75rem", mt: 0.3 }}>
                  {s.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* ── RIGHT PANEL ──────────────────────────────────────────────────────── */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          p: { md: 6, lg: 8 },
          background: "#fff",
          position: "relative",
        }}
      >
        {/* Subtle blobs */}
        <Box sx={{ position: "absolute", top: -70, right: -70, width: 240, height: 240, borderRadius: "50%", background: "radial-gradient(circle, rgba(102,126,234,0.07) 0%, transparent 70%)", pointerEvents: "none" }} />
        <Box sx={{ position: "absolute", bottom: -50, left: -50, width: 180, height: 180, borderRadius: "50%", background: "radial-gradient(circle, rgba(118,75,162,0.06) 0%, transparent 70%)", pointerEvents: "none" }} />

        <Box sx={{ width: "100%", maxWidth: 400, position: "relative", zIndex: 1 }}>

          {/* Get Started badge */}
          <Box sx={{ display: "flex", justifyContent: "center", mb: 5 }}>
            <Box sx={{
              display: "inline-flex", alignItems: "center", gap: 0.75,
              py: "8px", px: 2.5, borderRadius: "999px",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              boxShadow: "0 4px 14px rgba(102,126,234,0.45)",
              userSelect: "none",
            }}>
              <AutoAwesomeIcon sx={{ fontSize: 15, color: "#fff" }} />
              <Typography sx={{ fontWeight: 800, fontSize: "0.85rem", color: "#fff", letterSpacing: "0.04em" }}>
                GET STARTED
              </Typography>
            </Box>
          </Box>

          {/* Heading */}
          <Typography sx={{ fontWeight: 800, fontSize: "1.6rem", color: "#0f172a", mb: 0.75, letterSpacing: "-0.025em", lineHeight: 1.25 }}>
            Welcome 👋
          </Typography>
          <Typography sx={{ color: "#6b7280", fontSize: "0.88rem", lineHeight: 1.7, mb: 4 }}>
            New or returning — just continue with Google and you're in.{" "}
            <Box component="span" sx={{ color: "#667eea", fontWeight: 700 }}>No password needed.</Box>
          </Typography>

          {/* Trust badges */}
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 4 }}>
            <Chip icon={<SecurityIcon sx={{ fontSize: "13px !important" }} />} label="Secure & Encrypted" size="small" sx={{ background: "#f0fdf4", color: "#15803d", border: "1px solid #bbf7d0", fontWeight: 600, fontSize: "0.7rem", height: 26, "& .MuiChip-icon": { color: "#15803d" } }} />
            <Chip icon={<CheckCircleIcon sx={{ fontSize: "13px !important" }} />} label="No credit card" size="small" sx={{ background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe", fontWeight: 600, fontSize: "0.7rem", height: 26, "& .MuiChip-icon": { color: "#1d4ed8" } }} />
          </Box>

          {/* Error */}
          {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

          {/* Google button */}
          <Box sx={{ border: "1.5px solid #e5e7eb", borderRadius: "14px", p: "14px", background: "#fafafa", mb: 3, boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }} ref={btnContainerRef}>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}>
                <CircularProgress size={28} sx={{ color: "#667eea" }} />
              </Box>
            ) : (
              <GoogleLogin
                onSuccess={handleGoogle}
                onError={() => setError("Google authentication failed. Please try again.")}
                theme="outline"
                size="large"
                width={String(btnWidth - 28)}
                text="continue_with"
                shape="rectangular"
                logo_alignment="center"
              />
            )}
          </Box>

          {/* OR divider */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
            <Box sx={{ flex: 1, height: "1px", background: "#f0f0f0" }} />
            <Typography sx={{ color: "#d1d5db", fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.05em" }}>OR</Typography>
            <Box sx={{ flex: 1, height: "1px", background: "#f0f0f0" }} />
          </Box>

          {/* More options pill */}
          <Box sx={{ textAlign: "center", py: 1.5, borderRadius: "10px", border: "1.5px dashed #e5e7eb", color: "#9ca3af", fontSize: "0.78rem", mb: 5, userSelect: "none", letterSpacing: "0.01em" }}>
            More sign-in options coming soon
          </Box>

          {/* Legal */}
          <Typography sx={{ display: "block", textAlign: "center", color: "#9ca3af", lineHeight: 1.85, fontSize: "0.72rem" }}>
            New users get an account automatically.
            <br />
            By continuing you agree to our{" "}
            <Box component="span" onClick={() => navigate("/termsandconditions")} sx={{ color: "#667eea", cursor: "pointer", fontWeight: 600, "&:hover": { textDecoration: "underline" } }}>Terms of Service</Box>{" "}
            &amp;{" "}
            <Box component="span" onClick={() => navigate("/privacy-policy")} sx={{ color: "#667eea", cursor: "pointer", fontWeight: 600, "&:hover": { textDecoration: "underline" } }}>Privacy Policy</Box>.
          </Typography>

        </Box>
      </Box>

      {/* Phone Number Dialog */}
      <PhoneNumberDialog
        open={phoneDialogOpen}
        onSubmit={handlePhoneNumberSubmit}
        onClose={handlePhoneDialogClose}
        loading={loading}
      />
    </Box>
  );
};

export default Login;
